// Route → help screen key (spec 13 §B2, 12 §0). Screen keys are the second segment of help content keys:
// `order.{screen}.{kind}.{id}`. One screen may serve several routes; order matters (first match wins).
export const HELP_SCREEN_MAP: Array<[RegExp, string]> = [
  [/^\/supplier-sales\/new$/, "supplier-sales-new"],
  // The edit form is the same form (draft → send): it shares the create form's content.
  [/^\/supplier-sales\/edit\//, "supplier-sales-new"],
  [/^\/supplier-sales\/detail\//, "supplier-sales-detail"],
  [/^\/supplier-sales/, "supplier-sales-list"],
  // D16: батлах / татгалзах — Худалдан авалт (PO)
  [/^\/supplier-purchase\/detail\//, "supplier-purchase-detail"],
  [/^\/supplier-purchase/, "supplier-purchase-list"],
  // Buyer portal: «Захиалга» = the catalogue; the order form (edit) shares `order.buyer-sales-new` with OrderForm's helpNs.
  [/^\/buyer-sales\/new$/, "buyer-sales-catalog"],
  [/^\/buyer-sales\/product\//, "buyer-sales-product"],
  [/^\/buyer-sales\/quick-order/, "buyer-sales-quick-order"],
  [/^\/buyer-sales\/saved-lists/, "buyer-sales-saved-lists"],
  [/^\/buyer-sales\/promotions/, "buyer-sales-promotions"],
  [/^\/buyer-sales\/checkout/, "buyer-sales-checkout"],
  [/^\/buyer-sales\/success/, "buyer-sales-success"],
  [/^\/buyer-sales\/detail\//, "buyer-sales-detail"],
  [/^\/buyer-sales\/edit\//, "buyer-sales-new"],
  [/^\/buyer-sales/, "buyer-sales-list"],
  // The supplier pick-list's «Захиалга үүсгэх» opens the shared OrderForm (side="buyer"): same content as the buyer form.
  [/^\/buyer-customer\/order\//, "buyer-sales-new"],
  [/^\/buyer-customer\/detail\//, "buyer-customer-detail"],
  [/^\/buyer-customer/, "buyer-customer-list"],
  // «Харилцагчаар захиалах» is the supplier order form with the customer preselected: it shares the create form's content.
  [/^\/supplier-customer\/order\//, "supplier-sales-new"],
  [/^\/supplier-customer\/detail\//, "supplier-customer-detail"],
  [/^\/supplier-customer/, "supplier-customer-list"],
  [/^\/received-quotation\/./, "received-quotation-detail"],
  [/^\/received-quotation/, "received-quotation-list"],
  [/^\/supplier-drop-shipping\/.*detail\//, "supplier-drop-shipping-detail"],
  [/^\/supplier-drop-shipping/, "supplier-drop-shipping-list"],
  [/^\/supplier-delivery\/approve/, "supplier-delivery-approve"],
  [/^\/supplier-delivery\/detail\//, "supplier-delivery-detail"],
  [/^\/supplier-delivery/, "supplier-delivery-list"],
  [/^\/supplier-shipping\/detail\//, "supplier-shipping-detail"],
  [/^\/supplier-shipping/, "supplier-shipping-list"],
  [/^\/pending-deliveries/, "supplier-delivery-list"],
  [/^\/active-deliveries/, "supplier-delivery-list"],
  [/^\/failed-deliveries/, "failed-deliveries"],
  [/^\/delivery-routes\/new$/, "delivery-routes-new"],
  [/^\/delivery-routes\/./, "delivery-routes-detail"],
  [/^\/delivery-routes/, "supplier-delivery-list"],
  [/^\/handover\/./, "handover-detail"],
  // /handover is the hub's Хүлээлцэл tab on its own page: same content, the page sets `when: "HANDOVER"`.
  [/^\/handover/, "supplier-delivery-list"],
  // /picking redirects to the shipping hub's Зөрчилтэй ачаа tab.
  [/^\/picking/, "supplier-shipping-list"],
  [/^\/inbound/, "inbound"],
  [/^\/damaged-inventory\/turnover/, "damaged-inventory-turnover"],
  [/^\/damaged-inventory\/./, "damaged-inventory-detail"],
  [/^\/damaged-inventory/, "damaged-inventory-list"],
  [/^\/returns\/settings\/notification-templates/, "returns-settings-notification-templates"],
  [/^\/returns\/settings\/propose-templates/, "returns-settings-propose-templates"],
  [/^\/returns\/settings\/failed-operations/, "returns-settings-failed-operations"],
  [/^\/returns\/settings/, "returns-settings"],
  [/^\/returns\/dashboard/, "returns-dashboard"],
  [/^\/returns\/disputes/, "returns-disputes"],
  [/^\/returns\/transit-exceptions/, "returns-transit-exceptions"],
  [/^\/returns\/trust-scores\/./, "returns-trust-scores-detail"],
  [/^\/returns\/trust-scores/, "returns-trust-scores"],
  [/^\/returns\/prevention\/./, "returns-prevention-detail"],
  [/^\/returns\/prevention/, "returns-prevention"],
  [/^\/returns\/[^/]+\/warehouse-receipt/, "returns-warehouse-receipt"],
  [/^\/returns\/[^/]+\/off-system-verify/, "returns-off-system-verify"],
  [/^\/returns\/list/, "returns-list"],
  [/^\/returns\/[^/]+$/, "returns-detail"],
  [/^\/returns/, "returns-list"],
  [/^\/buyer-app\/my-returns\/new/, "buyer-app-my-returns-new"],
  [/^\/buyer-app\/my-returns\/./, "buyer-app-my-returns-detail"],
  [/^\/buyer-app\/my-returns/, "buyer-app-my-returns"],
  // Path-A return entry runs the same wizard; the order alias renders BuyerSalesDetailForm (variant="app").
  [/^\/buyer-app\/orders\/[^/]+\/return/, "buyer-app-my-returns-new"],
  [/^\/buyer-app\/orders\//, "buyer-sales-detail"],
  [/^\/buyer-app\/notifications/, "buyer-app-notifications"],
  [/^\/vehicle/, "vehicle"],
  [/^\/settings/, "settings"],
  [/^\/dashboard/, "dashboard"],
  [/^\/$/, "dashboard"],
]

export const resolveHelpScreen = (pathname: string): string | null =>
  HELP_SCREEN_MAP.find(([re]) => re.test(pathname))?.[1] ?? null

/** Drawer header line per screen (locale-independent for now; content `page.main.title` overrides). */
export const HELP_SCREEN_TITLES: Record<string, string> = {
  "buyer-sales-list": "Худалдан авалт · Жагсаалт",
  "buyer-sales-detail": "Худалдан авалт · Захиалгын дэлгэрэнгүй",
  "buyer-sales-new": "Захиалга · Захиалгын маягт",
  "buyer-sales-catalog": "Захиалга · Каталог",
  "buyer-sales-product": "Захиалга · Барааны дэлгэрэнгүй",
  "buyer-sales-quick-order": "Захиалга · Олноор нэмэх",
  "buyer-sales-saved-lists": "Захиалга · Хадгалсан жагсаалт",
  "buyer-sales-promotions": "Захиалга · Урамшуулал",
  "buyer-sales-checkout": "Захиалга · Баталгаажуулалт",
  "buyer-sales-success": "Захиалга · Илгээсэн захиалга",
  "buyer-customer-list": "Гэрээт нийлүүлэгч",
  "buyer-customer-detail": "Гэрээт нийлүүлэгч · Дэлгэрэнгүй",
  "received-quotation-list": "Үнийн санал",
  "received-quotation-detail": "Үнийн санал · Дэлгэрэнгүй",
  "buyer-app-my-returns": "Буцаалт",
  "buyer-app-my-returns-new": "Буцаалт · Шинэ буцаалт",
  "buyer-app-my-returns-detail": "Буцаалт · Буцаалтын дэлгэрэнгүй",
  "buyer-app-notifications": "Мэдэгдэл",
  "supplier-purchase-list": "Худалдан авалт · Жагсаалт",
  "supplier-purchase-detail": "Худалдан авалт · Захиалгын дэлгэрэнгүй",
  "supplier-sales-list": "Борлуулалт · Жагсаалт",
  "supplier-sales-detail": "Борлуулалт · Захиалгын дэлгэрэнгүй",
  "supplier-sales-new": "Борлуулалт · Шинэ захиалга",
  "supplier-sales-edit": "Борлуулалт · Захиалга засах",
  "supplier-drop-shipping-list": "Дропшиппинг · Жагсаалт",
  "supplier-drop-shipping-detail": "Дропшиппинг · Захиалгын дэлгэрэнгүй",
  "supplier-delivery-list": "Хүргэлт удирдлага",
  "supplier-delivery-approve": "Хүргэлт удирдлага · Ачилт баталгаажуулах",
  "supplier-delivery-detail": "Хүргэлт удирдлага · Хүргэлтийн дэлгэрэнгүй",
  "delivery-routes-new": "Хүргэлт удирдлага · Шинэ маршрут",
  "delivery-routes-detail": "Хүргэлт удирдлага · Маршрутын дэлгэрэнгүй",
  "supplier-customer-list": "Харилцагч",
  "supplier-customer-detail": "Харилцагч · Дэлгэрэнгүй",
  vehicle: "Тээвэр",
  settings: "Тохиргоо",
  dashboard: "Дашбоард",
  "supplier-shipping-list": "Ачилт удирдлага",
  "supplier-shipping-detail": "Ачилт удирдлага · Ачилтын хуудас",
  "inbound": "Орлого",
  "handover-detail": "Хүлээлцэл · Дэлгэрэнгүй",
  "returns-list": "Буцаалт",
  "returns-detail": "Буцаалт · Дэлгэрэнгүй",
  "returns-warehouse-receipt": "Буцаалт · Агуулахын хүлээн авалт",
  "returns-off-system-verify": "Буцаалт · Системээс гадуурх нэхэмжлэл",
  "returns-dashboard": "Буцаалт · Тойм",
  "returns-disputes": "Буцаалт · Маргаан",
  "returns-transit-exceptions": "Буцаалт · Тээвэрлэлтийн доголдол",
  "returns-trust-scores": "Буцаалт · Итгэлцлийн оноо",
  "returns-trust-scores-detail": "Буцаалт · Итгэлцлийн онооны дэлгэрэнгүй",
  "returns-prevention": "Буцаалт · Урьдчилан сэргийлэх",
  "returns-prevention-detail": "Буцаалт · Мэдэгдлийн дэлгэрэнгүй",
  "returns-settings": "Буцаалт · Тохиргоо",
  "returns-settings-notification-templates": "Буцаалт · Мэдэгдлийн загварууд",
  "returns-settings-propose-templates": "Буцаалт · Санал загварууд",
  "returns-settings-failed-operations": "Буцаалт · Амжилтгүй үйлдлүүд",
  "damaged-inventory-list": "Гэмтэлтэй бараа",
  "damaged-inventory-detail": "Гэмтэлтэй бараа · Дэлгэрэнгүй",
  "damaged-inventory-turnover": "Гэмтэлтэй бараа · Удаашрах нөөц",
}
